import { ImagePlus } from "./shapes";
import { FONT_URLS } from "@coso/core/constants";

export function debounce(func: (...args: any[]) => void, timeout: number) {
  let timer: NodeJS.Timeout;
  return (...args: any[]) => {
    clearTimeout(timer);
    timer = setTimeout(() => {
      func(...args);
    }, timeout);
  };
}

export async function loadFonts() {
  await document.fonts.ready;
  Object.entries(FONT_URLS).forEach(async ([font, url]) => {
    const fontFace = new FontFace(font, `url(${url})`);
    await fontFace.load();
    document.fonts.add(fontFace);
  });
}

export async function toDataURL(url: string): Promise<string> {
  return await fetch(url)
    .then((response) => response.blob())
    .then(
      (blob) =>
        new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        })
    );
}

export async function loadImage(this: any, src: string, properties: any = {}) {
  let url: string;
  let fallback = `<svg width='512' height='512' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'><rect width='100%' height='100%' fill='#00000000' /></svg>`;

  if (!src) {
    url = "data:image/svg+xml;base64," + btoa(fallback);
  } else if (src.startsWith("http") || src.startsWith("data:")) {
    url = src;
  } else if (src.startsWith("<svg")) {
    url = "data:image/svg+xml;base64," + btoa(src);
  } else {
    url = "/media-blob/" + src;
  }

  try {
    const img = document.createElement("img");

    const data = await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.crossOrigin = "anonymous";
      img.src = url;
    });

    const image = new ImagePlus(img, properties);
    this.sourceMap[image.getSrc()] = src;
    return image;
  } catch (e) {
    // Fallback handling
    const img = document.createElement("img");
    const fallbackUrl = "data:image/svg+xml;base64," + btoa(fallback);

    await new Promise((resolve) => {
      img.onload = resolve;
      img.src = fallbackUrl;
      img.crossOrigin = "anonymous";
    });

    const image = new ImagePlus(img, properties);
    this.sourceMap[image.getSrc()] = src;
    return image;
  }
}

export function isFunction(value: any): value is Function {
  return typeof value === "function";
}

export function isString(value: any): value is string {
  return typeof value === "string";
}

export function isNumber(value: any): value is number {
  return typeof value === "number";
}

export function isObject(value: any): value is Record<string, any> {
  return typeof value === "object";
}

export function isPromise(value: any): value is Promise<any> {
  return value instanceof Promise;
}

export async function getValue<T>(value: T | (() => T)): Promise<T> {
  return await (isFunction(value) ? value() : value);
}
